import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import QRCode from 'react-qr-code';
import { Circles } from 'react-loading-icons';
import NewProgress from '../../components/rating/NewProgress';
import QRBatchGenerator from './QRBatchGenerator';
import BusinessProducts from './BusinessProducts';
import {
  Globe,
  Mail,
  Phone,
  Layers,
  FileText,
  User,
  ArrowRight,
  PlusCircle,
  ShieldCheck,
} from 'lucide-react';

const BusinessProfile = () => {
  const token = localStorage.getItem('loginToken');
  const server_url = process.env.REACT_APP_API_URI;
  const navigate = useNavigate();

  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editedDescription, setEditedDescription] = useState('');
  const [reviewLink, setReviewLink] = useState('');
  const [isReviewCreated, setIsReviewCreated] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('No authentication token found.');
      setLoading(false);
      return;
    }

    let userData;
    try {
      userData = jwtDecode(token);
    } catch {
      setError('Invalid token.');
      setLoading(false);
      return;
    }

    if (!userData?.businessId) {
      setError('No business ID found.');
      setLoading(false);
      return;
    }

    const fetchBusinessData = async () => {
      try {
        const response = await axios.get(
          `${server_url}/business/${userData.businessId}`,
          {
            headers: { 'Content-Type': 'application/json' },
          },
        );
        setCompanyData(response.data.business);
        setEditedDescription(response.data.business.description);
      } catch {
        setError('Error fetching business data.');
      } finally {
        setLoading(false);
      }
    };

    fetchBusinessData();
  }, [token, server_url]);

  const formatImageDataURL = (image) => {
    if (!image || !image.data || !image.contentType) return null;
    const base64 =
      typeof image.data === 'string'
        ? image.data
        : Buffer.from(image.data).toString('base64');
    return `data:${image.contentType};base64,${base64}`;
  };

  // businessLogoURL was not used in the provided snippet, so it's removed.
  const adminProfileImageURL = companyData
    ? formatImageDataURL(companyData.profileImage)
    : null;

  const handleEditOpen = () => setEditOpen(true);
  const handleEditClose = () => setEditOpen(false);

  const handleDescriptionChange = (event) => {
    setEditedDescription(event.target.value);
  };

  const handleSaveDescription = async () => {
    try {
      await axios.put(
        `${server_url}/business/update-description/${companyData._id}`,
        {
          description: editedDescription,
        },
      );
      setCompanyData({ ...companyData, description: editedDescription });
      handleEditClose();
    } catch (error) {
      // Changed from `_` to `error` and updated error handling
      console.error('Error updating description:', error);
      setError('Error updating description.'); // Re-added setError for consistency
    }
  };

  const handleRedirect = async () => {
    try {
      const response = await fetch(`${server_url}/businessRating/generateId`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ businessId: companyData._id }),
      });

      const data = await response.json();

      if (response.ok) {
        const link = `${window.location.origin}/company-rating/${companyData._id}/${data.ratingId}`;
        setReviewLink(link);
        setIsReviewCreated(true);
      } else {
        console.error('Failed to create review link:', data);
      }
    } catch (err) {
      console.error('Error creating review link:', err);
    }
  };

  if (loading)
    return (
      <div className="flex justify-center items-center h-[60vh]">
        <Circles className="text-white" />
      </div>
    );
  if (error)
    return <p className="text-center text-red-500 text-lg mt-5">{error}</p>;
  if (!companyData)
    return (
      <p className="text-center text-white text-lg mt-5">
        No business data available.
      </p>
    );

  return (
    <>
      <div className="min-h-[calc(100vh-300px)] max-w-screen-xl mx-auto mt-6 mb-4">
        <h1 className="text-4xl font-bold text-secondary text-center mb-6">
          Business Profile
        </h1>
        <div className="bg-gray-900 rounded-3xl mx-6 lg:mx-12 overflow-hidden mb-8 border border-slate-800 shadow-2xl">
          {/* Banner Section */}
          <div className="relative h-48 w-full bg-gradient-to-r from-blue-700 via-indigo-800 to-slate-900 mb-5">
            <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]"></div>
            <div className="absolute bottom-4 right-6">
              <button
                className="flex items-center gap-2 px-5 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-md text-white border border-white/20 rounded-xl text-sm font-bold transition-all"
                onClick={handleEditOpen}
              >
                <FileText className="w-4 h-4" />
                Edit Profile
              </button>
            </div>
          </div>

          <div className="px-8 pb-10 -mt-16 relative">
            <div className="flex flex-col md:flex-row items-end gap-6 mb-8">
              <div className="relative shrink-0">
                <div className="w-40 h-40 rounded-full bg-slate-900 border-[6px] border-gray-900 overflow-hidden flex items-center justify-center shadow-2xl relative transition-transform hover:scale-[1.02] duration-300">
                  <img
                    src={adminProfileImageURL}
                    alt="Admin"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <div className="flex-1 mb-2">
                <div className="flex items-center gap-3 flex-wrap mb-2">
                  <h1 className="text-3xl md:text-5xl font-black text-white tracking-tight leading-none">
                    {companyData.name}
                  </h1>
                  <div className="flex items-center gap-1.5 px-3 py-1 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-full">
                    <ShieldCheck className="w-4 h-4" />
                    <span className="text-[10px] font-black uppercase tracking-widest">
                      Verified
                    </span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 text-slate-400">
                  <div className="flex items-center gap-2 bg-white/5 px-3 py-1.5 rounded-lg border border-white/10 backdrop-blur-sm">
                    <Layers className="w-4 h-4 text-blue-400" />
                    <span className="text-sm font-bold text-slate-200">
                      {Array.isArray(companyData.category)
                        ? companyData.category.join(' • ')
                        : companyData.category}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              {/* Main Content (Left Column) */}
              <div className="lg:col-span-2 space-y-10">
                {/* Description Box */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-slate-400 mb-2">
                    <FileText className="w-4 h-4" />
                    <span className="text-xs font-black uppercase tracking-[0.2em]">
                      Company Biography
                    </span>
                  </div>
                  <div className="bg-slate-800/20 border border-slate-700/30 rounded-2xl p-6 relative overflow-hidden group">
                    <div className="absolute top-0 left-0 w-1 h-full bg-blue-500 rounded-full opacity-50"></div>
                    <p className="text-slate-300 leading-relaxed text-lg italic font-medium">
                      {companyData.description ||
                        'This business has not provided a description yet.'}
                    </p>
                  </div>
                </div>

                {/* Products Section */}
                <BusinessProducts
                  businessId={companyData.businessId || companyData._id}
                  businessCategory={
                    Array.isArray(companyData.category)
                      ? companyData.category
                      : [companyData.category]
                  }
                />

                {/* Ratings Section */}
                <div className="relative group overflow-hidden bg-slate-900/40 border border-slate-700/50 rounded-[2rem] p-10 transition-all duration-500 hover:border-blue-500/30 shadow-2xl">
                  {/* Decorative background elements */}
                  <div className="absolute -top-24 -right-24 w-48 h-48 bg-blue-600/10 blur-[80px] rounded-full group-hover:bg-blue-600/20 transition-all duration-700"></div>
                  <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-indigo-600/10 blur-[80px] rounded-full group-hover:bg-indigo-600/20 transition-all duration-700"></div>

                  <div className="relative flex flex-col md:flex-row items-center justify-between gap-10">
                    <div className="flex-1 text-center md:text-left">
                      <div className="flex items-center justify-center md:justify-start gap-2 text-blue-400 mb-2">
                        <ShieldCheck className="w-5 h-5" />
                        <span className="text-xs font-black uppercase tracking-[0.3em]">
                          Trust Metrics
                        </span>
                      </div>
                      <h2 className="text-4xl md:text-5xl font-black text-white mb-4 tracking-tighter">
                        Overall{' '}
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400">
                          Score
                        </span>
                      </h2>
                      <p className="text-slate-400 text-lg font-medium leading-relaxed max-w-sm mx-auto md:mx-0">
                        Based on{' '}
                        <span className="text-white font-bold">
                          {companyData.totalReviews}
                        </span>{' '}
                        verified consumer responses from our community.
                      </p>
                    </div>

                    <div className="relative shrink-0">
                      <div className="absolute inset-0 bg-blue-500/20 blur-3xl rounded-full scale-75 animate-pulse"></div>
                      <div className="relative p-2 bg-slate-800/50 backdrop-blur-xl border border-white/5 rounded-full shadow-2xl">
                        <NewProgress kqRating={companyData.averageRating} />
                      </div>
                      <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-[10px] font-black uppercase tracking-widest px-4 py-1.5 rounded-full shadow-xl border border-blue-400/30 whitespace-nowrap">
                        Market Leader
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sidebar (Right Column) */}
              <div className="space-y-10">
                {/* Connectivity */}
                <div className="space-y-6">
                  <h3 className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">
                    Connectivity
                  </h3>
                  <div className="grid gap-3">
                    <div className="flex items-center gap-3 bg-slate-800/40 p-4 rounded-2xl border border-slate-700/30">
                      <div className="p-2.5 bg-blue-500/10 rounded-xl">
                        <Mail className="w-5 h-5 text-blue-400" />
                      </div>
                      <div>
                        <p className="text-[9px] text-slate-500 uppercase font-black tracking-widest leading-none mb-1">
                          Corporate Email
                        </p>
                        <p className="text-white font-bold text-sm">
                          {companyData.email}
                        </p>
                      </div>
                    </div>

                    {companyData.website && (
                      <div className="flex items-center gap-3 bg-slate-800/40 p-4 rounded-2xl border border-slate-700/30 group cursor-pointer hover:bg-slate-800/60 transition-colors">
                        <div className="p-2.5 bg-emerald-500/10 rounded-xl">
                          <Globe className="w-5 h-5 text-emerald-400" />
                        </div>
                        <div className="flex-1 overflow-hidden">
                          <p className="text-[9px] text-slate-500 uppercase font-black tracking-widest leading-none mb-1">
                            Official Website
                          </p>
                          <a
                            href={
                              companyData.website.startsWith('http')
                                ? companyData.website
                                : `https://${companyData.website}`
                            }
                            className="text-white font-bold text-sm hover:text-blue-400 transition-colors block truncate"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {companyData.website.replace(/^https?:\/\//, '')}
                          </a>
                        </div>
                      </div>
                    )}

                    {companyData.businessContact && (
                      <div className="flex items-center gap-3 bg-slate-800/40 p-4 rounded-2xl border border-slate-700/30">
                        <div className="p-2.5 bg-amber-500/10 rounded-xl">
                          <Phone className="w-5 h-5 text-amber-400" />
                        </div>
                        <div>
                          <p className="text-[9px] text-slate-500 uppercase font-black tracking-widest leading-none mb-1">
                            Direct Line
                          </p>
                          <p className="text-white font-bold text-sm">
                            {companyData.businessContact}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="space-y-6">
                  <h3 className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">
                    Operations
                  </h3>
                  <div className="space-y-4">
                    <button
                      onClick={() => navigate(`/businessquestion`)}
                      className="group flex flex-col gap-3 p-5 w-full bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl hover:shadow-xl hover:shadow-blue-500/20 transition-all border border-blue-400/20 text-left"
                    >
                      <PlusCircle className="w-6 h-6 text-white" />
                      <div>
                        <h4 className="text-white font-bold">
                          Private Questions
                        </h4>
                        <p className="text-blue-100/70 text-xs mt-1">
                          Tailor your rating forms for deep market insights.
                        </p>
                      </div>
                    </button>

                    <div className="p-5 bg-slate-800/50 rounded-2xl border border-slate-700/50 flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                        <User className="w-5 h-5 text-slate-400" />
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-500 uppercase font-black tracking-widest leading-none mb-1">
                          Administrator
                        </p>
                        <p className="text-slate-200 text-sm font-bold">
                          {companyData.firstName} {companyData.lastName}
                        </p>
                      </div>
                    </div>

                    <button
                      onClick={() => navigate(`/business/${companyData._id}`)}
                      className="group flex items-center justify-center gap-2 w-full px-6 py-4 bg-white text-slate-900 rounded-2xl font-black transition-all hover:bg-slate-100 shadow-xl"
                    >
                      Analytics & Reviews
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mx-4 px-4">
            {/* QR Batch Generator */}
            <QRBatchGenerator companyData={companyData} />

            {editOpen && (
              <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
                <div className="bg-gray-900 p-6 rounded-lg shadow-lg w-96">
                  <h2 className="text-white text-xl font-semibold">
                    Edit Description
                  </h2>
                  <textarea
                    className="w-full p-2 mt-3 bg-gray-800 text-white rounded-lg"
                    rows="4"
                    value={editedDescription}
                    onChange={handleDescriptionChange}
                  ></textarea>
                  <div className="flex justify-end mt-4 gap-3">
                    <button
                      className="bg-gray-500 text-white px-4 py-2 rounded-lg"
                      onClick={handleEditClose}
                    >
                      Cancel
                    </button>
                    <button
                      className="bg-green-500 text-white px-4 py-2 rounded-lg"
                      onClick={handleSaveDescription}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default BusinessProfile;
